// noinspection GrazieInspection,SpellCheckingInspection

/**
 * Shared scoring logic for the Nitter health tracker.
 *
 * Imported by:
 *   - Cloudflare Pages Functions under functions/api/*
 *   - The Vite dev mock server (src/dev/mock-api.ts)
 *
 * Keeping the math in one place guarantees dev matches prod.
 */

export const KEY_PREFIX = "host:";
export const ALPHA = 0.2; // EWMA weight of the newest observation (higher = reacts faster)
export const MAX_PING_MS = 5_000; // latency at/above this counts as the slowest
export const BIAS_LIMIT = 50; // manual preference is clamped to [-50, +50]
export const BIAS_DECAY = 0.98; // bias drifts back toward 0 each poll (set to 1 to disable)
export const SKIP_PARAM = "__skip";
export const USER_FAIL_PENALTY = -8;
export const FAIL_DEDUPE_TTL = 600; // seconds; one report per IP+domain in this window

export type HostSource = {
  domain: string;
  url: string;
  healthy: boolean;
  ping_avg?: number | null;
  ping_max?: number | null;
  ping_min?: number | null;
  recent_pings?: (number|null)[];
    recent_checks?: [string, boolean][];
  points?: number | null;
  rss?: boolean | null;
  version_state?: string | null;
  version?: string | null;
    is_upstream?: boolean | null;
    is_latest_version?: boolean | null;
    is_bad_host?: boolean | null;
    country?: string | null;
    __show_last_seen?: boolean | null;
    connectivity?: string | null;
  version_url?: string | null;
  last_healthy?: string | null;
  healthy_percentage_overall?: number | null;
};

export type HostEntry = {
  domain: string;
  url: string;
  score: number;
  bias: number;
  effective: number;
  samples?: number;
  consecutive_failures?: number;
  healthy: boolean;
  last_healthy: string | null;
  ping_avg: number | null;
  rss: boolean;
  version_state: string | null;
  source_healthy_pct?: number | null;
  /** True if the upstream's last poll returned X-Frame-Options: DENY (or a CSP frame-ancestors directive that doesn't include our origin). */
  blocks_iframe?: boolean;
  updated: string;
};

export const clamp = (n: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, n));

export const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Turn one uptime-JSON host entry into a 0..100 observation. */
export function observe(host: HostSource): number {
  if (!host.healthy) return 0;
  const ping = host.ping_avg ?? MAX_PING_MS;
  const latency = 1 - clamp(ping / MAX_PING_MS, 0, 1);
  return 70 + latency * 25 + (host.rss ? 5 : 0);
}

/** Effective score used for ranking: running score + manual bias. */
export function effective(entry: { score: number; bias: number }): number {
  return clamp(entry.score + entry.bias, 0, 100);
}

/** Compute the next entry from a poll, given the previous entry (or null for new hosts). */
export function nextEntry(
  host: HostSource,
  prev: HostEntry | null,
  now: string,
): HostEntry {
  const obs = observe(host);
  const prevScore =
    prev?.score ?? clamp(host.healthy_percentage_overall ?? 50, 0, 100);
  // EWMA — same formula regardless of whether prev exists, just different seed.
  const score = prevScore * (1 - ALPHA) + obs * ALPHA;
  // noinspection PointlessBooleanExpressionJS
    const entry: HostEntry = {
    domain: host.domain,
    url: host.url,
    score: round1(score),
    bias: round1((prev?.bias ?? 0) * BIAS_DECAY),
    effective: 0,
    samples: (prev?.samples ?? 0) + 1,
    consecutive_failures: host.healthy
      ? 0
      : (prev?.consecutive_failures ?? 0) + 1,
    healthy: !!host.healthy,
    last_healthy: host.last_healthy ?? prev?.last_healthy ?? null,
    ping_avg: host.ping_avg ?? null,
    rss: !!host.rss,
    version_state: host.version_state ?? null,
    source_healthy_pct: host.healthy_percentage_overall ?? null,
    // Preserved across polls; only updated by pollUptime() when it does the actual HEAD check against the upstream.
    blocks_iframe: prev?.blocks_iframe ?? false,
    updated: now,
  };
  entry.effective = round1(effective(entry));
  return entry;
}

/** Apply a manual bias delta to an entry (clamped, returns a NEW object). */
export function withAdjustedBias(prev: HostEntry | null, domain: string, delta: number, now: string): HostEntry {
  const base: HostEntry =
    prev ?? {
      domain,
      url: `https://${domain}`,
      score: 50,
      bias: 0,
      effective: 0,
      samples: 0,
      consecutive_failures: 0,
      healthy: false,
      last_healthy: null,
      ping_avg: null,
      rss: false,
      version_state: null,
      source_healthy_pct: null,
      blocks_iframe: false,
      updated: now,
    };
  const next: HostEntry = {
    ...base,
    bias: round1(clamp(base.bias + delta, -BIAS_LIMIT, BIAS_LIMIT)),
    updated: now,
  };
  next.effective = round1(effective(next));
  return next;
}

/** Weighted random pick: higher effective score => more likely, but not deterministic. */
export function pickWeighted<T extends { effective: number }>(
  candidates: T[],
): T {
  const weights = candidates.map((e) => Math.max(e.effective, 1) ** 2);
  let r = Math.random() * weights.reduce((a, b) => a + b, 0);
  for (let i = 0; i < candidates.length; i++) {
    r -= weights[i];
    if (r <= 0) return candidates[i]!;
  }
  return candidates[candidates.length - 1]!;
}

export const esc = (s: unknown): string =>
  String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );

/**
 * Inspect a fetch Response and decide whether the page can be embedded
 * in an iframe from any origin other than the upstream's own.
 *
 * Returns one of:
 *   - "deny"      : X-Frame-Options: DENY  → can NEVER be iframed
 *   - "sameorigin": X-Frame-Options: SAMEORIGIN (or CSP frame-ancestors 'self')
 *                   → can't be iframed from our origin
 *   - "allow"     : no blocking headers, or frame-ancestors includes '*'
 */
export type FramePolicy = "deny" | "sameorigin" | "allow";

export function detectFramePolicy(res: Response): FramePolicy {
  const xfo = (res.headers.get("x-frame-options") ?? "").toLowerCase();
  if (xfo === "deny") return "deny";
  if (xfo === "sameorigin" || xfo === "allow-from") return "sameorigin";

  const csp = res.headers.get("content-security-policy") ?? "";
  if (csp) {
    const m = csp.match(/frame-ancestors\s+([^;]+)/i);
    if (m) {
      const sources = m[1]!.trim().split(/\s+/).map((s) => s.toLowerCase());
      if (sources.includes("'none'")) return "deny";
      if (sources.includes("*")) return "allow";
      // 'self' or specific origins — our proxy origin is different, so block.
      return "sameorigin";
    }
  }
  return "allow";
}

/**
 * Check an upstream URL to see whether it allows iframe embedding.
 * Returns the FramePolicy. Never throws — on any error, returns "allow"
 * (so we don't downgrade a working host because of a transient failure).
 *
 * Uses GET rather than HEAD: some Nitter servers only attach
 * X-Frame-Options / CSP to GET responses, which made the old HEAD probe
 * wrongly classify them as embeddable. We abort the body as soon as the
 * headers arrive — we only need the response headers.
 *
 * @param upstreamUrl  The URL to check
 * @param timeoutMs    Hard timeout (default 8s)
 */
export async function probeFramePolicy(
  upstreamUrl: string,
  timeoutMs = 8000,
): Promise<FramePolicy> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(upstreamUrl, {
      method: "GET",
      signal: ctrl.signal,
      // Don't follow redirects — a 3xx response rarely carries the frame
      // policy of the final destination, and the browser will re-check on
      // the redirected URL anyway.
      redirect: "manual",
      headers: {
        "user-agent": "nitter-health-prober/1.0",
        accept: "text/html,*/*",
      },
    });
    // Cancel the body immediately — headers are all we need.
    try {
      await res.body?.cancel();
    } catch {
      /* body already consumed or unsupported — ignore */
    }
    return detectFramePolicy(res);
  } catch {
    return "allow"; // don't penalize on transient errors
  } finally {
    clearTimeout(timer);
  }
}
